import { defineStore } from "pinia";
import type { Classroom } from "~/types/classroom";
import type { Media } from "~/types/media";
import toastMessages from "~/data/content/toast-messages.json";

// route.params.slug is `string | string[]`. Helper narrows to `string`.
const paramSlug = (value: string | string[] | undefined): string =>
   Array.isArray(value) ? (value[0] ?? "") : (value ?? "");

export const useClassesStore = defineStore("classes", () => {
   const $apiFetch = useNuxtApp().$apiFetch as typeof $fetch;
   const { logInfo, logError } = useDebugger();
   const { setPageTitle } = useBreadcrumbs();
   const { addToast } = useToast();

   const route = useRoute();
   // >> STATE
   const allClasses = ref<Classroom.UserClassesResponse | null>(null);
   const activeClasses = ref<Classroom.Simplified[] | null>(null);
   const archivedClasses = ref<Classroom.Simplified[] | null>(null);
   const currentClass = ref<Classroom.Resource | null>(null);

   // >> ACTIONS
   const queryClasses = async (): Promise<
      Classroom.UserClassesResponse | null
   > => {
      const data = await $apiFetch<Classroom.UserClassesResponse>(
         `users/classes`,
      );

      if (data) {
         allClasses.value = data;
         activeClasses.value = data.active;
         archivedClasses.value = data.archived;
      }
      logInfo(
         "queryClasses",
         allClasses.value,
         activeClasses.value,
         archivedClasses.value,
      );
      return data ?? null;
   };

   const queryClass = async (slug: string): Promise<Classroom.Resource> => {
      const data = await $apiFetch<Classroom.Resource>(`/classes/${slug}`);
      currentClass.value = data;
      logInfo("queryClass", data);
      setPageTitle(data.title);
      return data;
   };

   const createClass = async (
      formData: FormData,
   ): Promise<Classroom.CreateResponse> =>
      await $apiFetch<Classroom.CreateResponse>(`/classes`, {
         method: "POST",
         body: formData,
      }).then(async (data) => {
         await queryClasses();
         logInfo("Class created!", data);
         return data;
      });

   const createClassNote = async (
      formData: FormData,
   ): Promise<Classroom.AddNotecardResponse> => {
      const slug = paramSlug(route.params.slug);
      const data = await $apiFetch<Classroom.AddNotecardResponse>(
         `/classes/${slug}/notecards`,
         { method: "POST", body: formData },
      );
      logInfo("Class Note Created", data);
      await queryClass(slug);
      addToast(toastMessages.createClassNote.success as Toast);
      return data;
   };

   const removeClassNote = async (
      notecardId: number,
   ): Promise<ApiSuccess> => {
      const slug = paramSlug(route.params.slug);
      const data = await $apiFetch<ApiSuccess>(
         `/classes/${slug}/notecards/${notecardId}`,
         { method: "DELETE" },
      );
      logInfo("Class Note Removed", data);
      await queryClass(slug);
      addToast(toastMessages.removeClassNote.success as Toast);
      return data;
   };

   const uploadImage = async (
      formData: FormData,
   ): Promise<Classroom.UploadHeroResponse> => {
      const slug = paramSlug(route.params.slug);
      const data = await $apiFetch<Classroom.UploadHeroResponse>(
         `/classes/${slug}/header`,
         { method: "POST", body: formData },
      );
      logInfo("uploadImage", data);
      if (currentClass.value) {
         currentClass.value.image = data.image;
      }
      return data;
   };

   const removeImage = async (_slug?: string): Promise<ApiSuccess> => {
      const slug = paramSlug(route.params.slug);
      const data = await $apiFetch<ApiSuccess>(`/classes/${slug}/header`, {
         method: "DELETE",
      });
      logInfo("removeImage", data);
      if (currentClass.value) {
         currentClass.value.image = null;
      }
      return data;
   };

   const archiveClass = async (
      _slug?: string,
   ): Promise<Classroom.Resource> => {
      const slug = paramSlug(route.params.slug);
      const data = await $apiFetch<Classroom.Resource>(
         `/classes/${slug}/archive`,
         { method: "PATCH" },
      );
      logInfo("archiveClass", data);
      currentClass.value = data;
      addToast(toastMessages.archiveClass.success as Toast);
      return data;
   };

   const updateClassSettings = async (
      formData: FormData,
   ): Promise<Classroom.Resource> => {
      const slug = paramSlug(route.params.slug);
      const data = await $apiFetch<Classroom.Resource>(`/classes/${slug}`, {
         method: "PATCH",
         body: formData,
      });
      logInfo("updateClassSettings", data);
      currentClass.value = data;
      addToast(toastMessages.updateClassSettings.success as Toast);
      return data;
   };

   const assignToClass = async (
      type: "teachables" | "media" | "units",
      slug: string | number,
      classroom_id?: number,
      method: "POST" | "DELETE" = "POST",
      mediaType?: "video" | "audio" | null,
   ): Promise<ApiSuccess | false> => {
      return await $apiFetch<ApiSuccess>(`${type}/${slug}/assign`, {
         method,
         body: {
            classroom_id,
            type: mediaType, // currently only video is persisted server-side
         },
      })
         .then(async (data) => {
            await queryClasses();
            if (route.name === "class") {
               await queryClass(paramSlug(route.params.slug));
            }
            return data;
         })
         .catch((err) => {
            logError("ERROR", err);
            return false as const;
         });
   };

   const saveClassResources = async (
      formData: Array<{ id: number; type: string; sort: number }>,
   ): Promise<ApiSuccess | unknown> => {
      const slug = paramSlug(route.params.slug);
      return await $apiFetch<ApiSuccess>(`classes/${slug}/modules/sort`, {
         method: "PATCH",
         body: { modules: formData },
      })
         .then((data) => {
            addToast({
               message: "Order has been successfully updated",
               messageEmoji: "✅",
               status: "success",
               duration: 5000,
            });
            return data;
         })
         .catch((err) => err);
   };

   const completeClassResource = async (
      slug: string,
      data: { teachable_id: number; completed: boolean },
   ): Promise<ApiSuccess> =>
      await $apiFetch<ApiSuccess>(`classes/${slug}/modules`, {
         method: "PATCH",
         body: data,
      }).then(async (res) => {
         await queryClass(slug);
         return res;
      });

   return {
      allClasses,
      activeClasses,
      archivedClasses,
      queryClasses,
      queryClass,
      currentClass,
      createClass,
      createClassNote,
      removeClassNote,
      assignToClass,
      uploadImage,
      removeImage,
      updateClassSettings,
      archiveClass,
      saveClassResources,
      completeClassResource,
   };
});
